import MUITable from "Components/UI/Tables/MUITable";
import { useInvoices } from "Context/Invoices";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Checkbox, Grid } from "@mui/material";
import { price2StringPrice } from "utils/priceformatter";

import {
  getParseStatusLabelById,
  getSystemStatusLabelById,
} from "utils/labels";
import cutStrAtAndAddDots from "utils/cutStrAtAndAddDots";
import CommonChip from "Components/UI/Tables/CommonChip";
import {
  getParseStatusColorByLabel,
  getSystemStatusColorByLabel,
} from "utils/colors";
import { sendToCollection } from "API/swesum";
import { LoadingButton } from "@mui/lab";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import useTableOptions from "Hooks/useTableOptions";
import { formatPrice } from "utils/priceformatter";
import ConfirmModal from '../processInvoices/ConfirmModal';
const createRows = (invoices) => {
  return invoices.map((i) => {
    return [
      i.Id, //For checkbox
      { label: i.ExternalInvoiceNo, id: i.Id },
      i.EndCustomerExternalCustomerNo,
      { label: i.EndCustomerName, id: i.EndCustomerId },
      i.ClientNumber,
      { label: i.ClientName, id: i.ClientId },
      formatPrice(i.Price === null ? 0 : i.Price),
      formatPrice(i.RemainingAmount === null ? 0 : i.RemainingAmount),
      i.OCR,
      i.OriginalFfdDate,
      getParseStatusLabelById(i.ParseStatus),
      cutStrAtAndAddDots(i.ParseMessage, 45),
      getSystemStatusLabelById(i.SystemStatus),
      i.CreateTime,
      i.CollectionWaitCreateDate,
    ];
  });
};

const columnsToHide = [
  "Kontrollstatus",
  "Kontrollmeddelande",
  "Status",
  "Skapad",
];

const CollectionInvoices = () => {
  const { fetchInvoicesForCollection, invoices } = useInvoices();
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const { openSuccess, openError } = usePopupNotificationContext();
  const [rows, setRows] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const { handleColumnShow, isColDisplayed, isNew } = useTableOptions(
    "CollectionInvoicesTable"
  );

  useEffect(() => {
    fetchInvoicesForCollection();
  }, []);

  useEffect(() => {
    setRows(createRows(invoices));
  }, [invoices]);

  useEffect(() => {
    if (isNew) {
      for (const col of columnsToHide) {
        handleColumnShow(col, "remove");
      }
    }
  }, [isNew]);

  const asyncConfirmCollection = async () => {
    setModalLoading(true);
    const body = [...selectedInvoiceIds];
    setButtonLoading(true);
    const res = await sendToCollection(body);
    if (res.status === 201) {
      openSuccess(res.data.Message);
      setSelectedInvoiceIds(new Set());
      fetchInvoicesForCollection();
    } else {
      openError(res.data.Message);
    }
    setModalLoading(false);
    setButtonLoading(false);
    setOpenConfirmModal(false);
  };

  const handleCheckBoxChange = (value) => {
    const newSelectedInvoiceIds = new Set(selectedInvoiceIds);
    if (selectedInvoiceIds.has(value)) {
      newSelectedInvoiceIds.delete(value);
    } else {
      newSelectedInvoiceIds.add(value);
    }
    setSelectedInvoiceIds(newSelectedInvoiceIds);
  };

  const checkAll = () => {
    if (selectedInvoiceIds.size === invoices.length) {
      setSelectedInvoiceIds(new Set());
      return;
    }
    const newSelectedInvoiceIds = new Set(selectedInvoiceIds);
    for (const invoice of invoices) {
      if (selectedInvoiceIds.has(invoice.Id)) {
        continue;
      }
      newSelectedInvoiceIds.add(invoice.Id);
    }
    setSelectedInvoiceIds(newSelectedInvoiceIds);
  };

  const isItemSelected = (value) => {
    return selectedInvoiceIds.has(value);
  };

  const columns = [
    {
      name: "",
      options: {
        filter: false,
        customHeadRender: (columnMeta) => (
          <th
            key={columnMeta.index}
            style={{ borderBottom: "1px solid lightgray" }}
          >
            <Checkbox
              checked={selectedInvoiceIds.size === invoices.length}
              onClick={checkAll}
            />
          </th>
        ),
        customBodyRender: (value) => {
          return (
            <tr style={{ display: "flex", justifyContent: "center" }}>
              <Checkbox
                checked={isItemSelected(value)}
                onChange={() => handleCheckBoxChange(value)}
              />
            </tr>
          );
        },
      },
    },
    {
      label: "Fakturanummer",
      name: "Title",
      options: {
        display: isColDisplayed("Title"),
        filter: false,
        customBodyRender: (value) => {
          return <Link to={`/invoices/${value.id}`}>{value.label}</Link>;
        },
      },
    },
    {
      name: "Kundnummer",
      options: {
        display: isColDisplayed("Kundnummer"),
        filter: true,
      },
    },
    {
      name: "Kundnamn",
      options: {
        display: isColDisplayed("Kundnamn"),
        filter: false,
        empty: false,
        customBodyRender: (value) => {
          return <Link to={`/endcustomers/${value.id}`}>{value.label}</Link>;
        },
      },
    },
    {
      label: "Klientnummer",
      options: {
        display: isColDisplayed("ClientId"),
        filter: true,
      },
    },
    {
      name: "Klientnamn",
      options: {
        display: isColDisplayed("Klientnamn"),
        filter: false,
        empty: false,
        customBodyRender: (value) => {
          return <Link to={`/clients/${value.id}`}>{value.label}</Link>;
        },
      },
    },
    {
      name: "Ursprungligt belopp",
      options: {
        display: isColDisplayed("Ursprungligt belopp"),
        filter: false,
      },
    },
    {
      name: "Återstående belopp",
      options: {
        display: isColDisplayed("Återstående belopp"),
        filter: false,
      },
    },
    {
      name: "OCR",
      options: {
        display: isColDisplayed("OCR"),
        filter: false,
      },
    },
    {
      name: "FFD",
      options: {
        display: isColDisplayed("FFD"),
        filter: false,
      },
    },
    {
      name: "Kontrollstatus",
      options: {
        display: isColDisplayed("Kontrollstatus"),
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getParseStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Kontrollmeddelande",
      options: {
        display: isColDisplayed("Kontrollmeddelande"),
        filter: false,
      },
    },
    {
      name: "Status",
      options: {
        display: isColDisplayed("Status"),
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getSystemStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Skapad",
      options: {
        display: isColDisplayed("Skapad"),
        filter: false,
      },
    },
    {
      name: "Inkassodatum",
      options: {
        display: isColDisplayed("Inkassodatum"),
        filter: true,
      },
    },
  ];

  const options = {
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    filterType: "dropdown",
    responsive: "standard",
    rowsPerPage: 30,
    rowsPerPageOptions: [10, 30, 100],
    downloadOptions: {
      separator: ";",
      filename: "ready_for_collection.csv",
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      const csvData = data.map(({ data, index }) => {
        const rowData = data.map((r1, index) => {
          if (typeof r1 === "object" && r1.label !== null) {
            return r1.label;
          }
          return r1;
        });
        return { index: index, data: rowData };
      });
      return "\uFEFF" + buildHead(columns) + buildBody(csvData);
    },
  };

  return (
    <div style={{ padding: "20px" }}>
      <MUITable
        options={options}
        title={"Faktura inkasso"}
        data={rows}
        columns={columns}
      />
      <Grid item container md={12} justifyContent="flex-end" mt={2}>
        <Grid item>
          <LoadingButton
            loading={buttonLoading}
            onClick={() => setOpenConfirmModal(true)}
            disabled={selectedInvoiceIds.size === 0}
            variant="contained"
          >
            Exportera till inkasso
          </LoadingButton>
        </Grid>
      </Grid>
      <ConfirmModal
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        title={"Vill du skicka " + selectedInvoiceIds.size + " antal fakturor på inkasso?"}
      >      
        <div
          style={{
            display: "flex",
            marginTop: "50px",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <LoadingButton
            onClick={() => asyncConfirmCollection()}
            loading={modalLoading}
            variant="contained"
          >
            Ja
          </LoadingButton>
          <LoadingButton onClick={() => setOpenConfirmModal(false)}>
            Nej
          </LoadingButton>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default CollectionInvoices;
